import { BaseSyntheticEvent, useCallback, useState } from 'react';
import { useAlert } from 'react-alert';
import { FormState, UseFormRegister } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import I18n from 'i18n-js';

import { passwordLogin } from '@lootboy/lib/actions/AuthActions';
import { getAppleAuthUrl } from '@lootboy/lib/api/auth';
import {
  ELoginWithEmailFormField,
  TLoginWithEmailFieldValues,
  useLoginWithEmailForm,
} from '@lootboy/lib/forms/useLoginWithEmailForm';
import { TStore } from '@lootboy/lib/types/store';

import Alert from '~/components/Alert';
import { EPlatform } from '~/components/molecules/PlatformButton';

import { useAppDispatch } from '~/redux/store/useAppDispatch';
import { parseLootBoyError } from '~/utilities/errors';

const LOGIN_CAPTCHA_REQUIRED_ERROR = 'Api_CardController_error_app_not_updated';
export const LOGIN_INCORRECT_PASSWORD_ERROR =
  'Api_AuthController_error_invalid_password';

type TLoginViewState = {
  form: {
    register: UseFormRegister<TLoginWithEmailFieldValues>;
    onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
    errors: FormState<TLoginWithEmailFieldValues>['errors'];
    isSubmitEnabled: boolean;
    submittedEmail: string | undefined;
  };
  captcha: {
    isRequired: boolean;
    onChange: (key: string) => Promise<void>;
    dismiss: () => void;
  };
  authWithProvider: (provider: EPlatform) => void;
};

const AUTH_PROVIDERS_METHODS = {
  [EPlatform.APPLE]: getAppleAuthUrl,
  [EPlatform.TWITTER]: 'twitter',
  [EPlatform.TWITCH]: 'twitch',
  [EPlatform.STEAM]: 'steam',
  [EPlatform.FACEBOOK]: 'facebook',
  [EPlatform.GOOGLE]: 'google',
} as const;

/**
 * Used to get a URL for authentication using a social provider. When using Apple as the social login provider
 * there needs to be an additional network request to get the URL and the function could throw.
 *
 * @throws {{ message: string, messageId: string }}
 */
export async function getAuthProviderUri(
  provider: EPlatform,
  apiUrl: string,
  captchaKey: string,
): Promise<string> {
  const authMethod = AUTH_PROVIDERS_METHODS[provider];
  if (typeof authMethod === 'string') {
    return `${apiUrl}/v1/auth/${authMethod}Login/start?g-recaptcha-response=${captchaKey}`;
  }

  const response = await authMethod(apiUrl);
  return response?.authorizationUri;
}

export function useLoginView(): TLoginViewState {
  const alert = useAlert();
  const location = useLocation<{ email?: string } | undefined>();
  const apiUrl = useSelector<TStore, string>(({ auth }) => auth.apiUrl);
  const dispatch = useAppDispatch();
  const [formValues, setFormValues] = useState<
    TLoginWithEmailFieldValues | undefined
  >();
  const [isCaptchaRequired, setCaptchaRequired] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<
    EPlatform | undefined
  >();

  const {
    register,
    onFormSubmit,
    formState: { errors },
    isSubmitEnabled,
    setError,
  } = useLoginWithEmailForm(async (loginFormValues) => login(loginFormValues), {
    defaultValues: {
      [ELoginWithEmailFormField.EMAIL_OR_USERNAME]: location.state?.email ?? '',
    },
  });

  const login = useCallback(
    async (values: TLoginWithEmailFieldValues, captchaKey?: string) => {
      try {
        await dispatch(
          passwordLogin(
            values[ELoginWithEmailFormField.EMAIL_OR_USERNAME],
            values[ELoginWithEmailFormField.PASSWORD],
            captchaKey,
          ),
        );
      } catch (err) {
        const error = parseLootBoyError(err, I18n.t('common_login_failed'));
        switch (error.messageId) {
          case LOGIN_CAPTCHA_REQUIRED_ERROR: {
            setFormValues(values);
            setCaptchaRequired(true);
            break;
          }
          case LOGIN_INCORRECT_PASSWORD_ERROR: {
            setError(ELoginWithEmailFormField.PASSWORD, {
              message: error.messageId,
            });
            break;
          }
          default: {
            alert.error(
              <Alert
                title={I18n.t('common_error')}
                body={error.message}
              />,
            );
          }
        }
      }
    },
    [alert, dispatch, setError],
  );

  const onCaptchaChange = useCallback(
    async (key: string) => {
      if (selectedProvider) {
        try {
          window.location.href = await getAuthProviderUri(
            selectedProvider,
            apiUrl,
            key,
          );
        } catch (err) {
          const error = parseLootBoyError(err, I18n.t('common_login_failed'));
          alert.error(
            <Alert
              title={I18n.t('common_error')}
              body={error.message}
            />,
          );
        }
        return;
      }
      if (formValues) {
        await login(formValues, key);
      }
    },
    [alert, apiUrl, formValues, login, selectedProvider],
  );

  const dismissCaptcha = useCallback(() => {
    setCaptchaRequired(false);
    setFormValues(undefined);
    setSelectedProvider(undefined);
  }, []);

  const authWithProvider = useCallback((provider: EPlatform) => {
    setSelectedProvider(provider);
    setCaptchaRequired(true);
  }, []);

  return {
    form: {
      register,
      onSubmit: onFormSubmit,
      errors,
      isSubmitEnabled,
      submittedEmail: formValues?.emailOrUsername,
    },
    captcha: {
      isRequired: isCaptchaRequired,
      dismiss: dismissCaptcha,
      onChange: onCaptchaChange,
    },
    authWithProvider,
  };
}
